import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '../auth/authStore.js';
import { supabase } from '../lib/supabase.js';
import { getCourseOutline, getCourseTree, PHRASES_COURSE_ID } from '../content/course.js';
import {
  buildFreeAccess,
  derivedStatus,
  hasProAccess,
  lessonAccessState,
  proAccessState,
  type AccessState,
  type CurrentSubscriptionRow,
  type DerivedStatus,
  type FreeAccessShape,
} from './access.js';
import { EVERYTHING_FREE } from './freeMode.js';

/**
 * React-Query hooks that wrap the pure access helpers in `./access.ts`.
 * Components import from here; no component reaches into Supabase directly.
 */

export const SUBSCRIPTION_KEY = (userId: string) => ['subscription', userId];

async function fetchCurrentSubscription(userId: string): Promise<CurrentSubscriptionRow | null> {
  const { data, error } = await supabase
    .from('current_subscription')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  // A missing row is NOT an error — `maybeSingle()` returns `data: null` for
  // it, and that null legitimately means "free tier". A real error (offline,
  // 5xx, RLS) must throw: swallowing it into `null` told every caller the
  // paying user has no subscription, and React Query could not retry a
  // "successful" read. Downgrade a customer only on the server's word.
  if (error) throw error;
  return data;
}

export function useSubscription(): UseQueryResult<CurrentSubscriptionRow | null> {
  const { user } = useAuth();
  return useQuery({
    queryKey: user ? SUBSCRIPTION_KEY(user.id) : ['subscription', 'anon'],
    queryFn: () => fetchCurrentSubscription(user!.id),
    enabled: !!user,
    staleTime: 30 * 1000,
  });
}

export function useDerivedSubscriptionStatus(): DerivedStatus {
  const { data } = useSubscription();
  return derivedStatus(data ?? null);
}

export function useHasProAccess(): boolean {
  const { data } = useSubscription();
  return hasProAccess(data ?? null);
}

/** Memoized free-access set (course structure is bundled, so it never
 *  changes at runtime). Free of any subscription data — pure content config:
 *  the main course's first skill + lesson 1 of every phrases level. The
 *  phrases side reads the course OUTLINE (unit → first skill → first lesson
 *  id), so it is right before the lazy phrases lessons are loaded — and
 *  never triggers that load. */
export function useFreeAccess(): FreeAccessShape {
  return useMemo(
    () =>
      buildFreeAccess(getCourseTree(), getCourseOutline(PHRASES_COURSE_ID), {
        everything: EVERYTHING_FREE,
      }),
    [],
  );
}

/**
 * Can the current user open this lesson right now — 'loading' | 'yes' | 'no'.
 *
 * TRI-STATE ON PURPOSE. The boolean version answered "no" while the
 * subscription read was still in flight, so every cold load of a Pro lesson
 * showed a paying subscriber the paywall (and counted a `paywall_shown`
 * against them) until the row landed a moment later. Callers must render a
 * loading state for 'loading' — never the paywall.
 */
export function useCanPlayLesson(lessonId: string): AccessState {
  const { data, isPending, isError } = useSubscription();
  const access = useFreeAccess();
  return lessonAccessState({ data, isPending, isError }, access, lessonId);
}

/** Pro access as a tri-state, for surfaces that DECORATE content with a lock
 *  (the skill tree). Same race as `useCanPlayLesson`: painting padlocks over
 *  a subscriber's tree for a frame is a bug, so hold the decoration until
 *  the answer is known. Screens that only describe the account (Profile,
 *  awards) keep using `useHasProAccess()`. */
export function useProAccessState(): AccessState {
  const { data, isPending, isError } = useSubscription();
  return proAccessState({ data, isPending, isError });
}
